import { useMemo, useState } from 'react'
import { AreaChart, Area, Tooltip, ResponsiveContainer } from 'recharts'
import { formatCost } from '@lens/lib/decode'
import { makeDateTickFormatter, formatDateFull } from '@lens/lib/charts'
import type { DailyCost } from '@lens/types/claude'
import { CHART_TOOLTIP_STYLE, ChartGrid, ChartXAxis, ChartYAxis, colorForModel, shortModelName } from '../chart-primitives'
import { Card, CardContent } from '../../../card'
import { SectionTitle } from '../../section-title'
import { StatStrip, type StatStripItem } from '../../dashboard/stat-strip'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../select'

interface Props {
 daily: DailyCost[]
 /** Cost summary metrics, folded into the card head as a banded strip
  *  (StatStrip variant="table") so the numbers read as part of this chart
  *  rather than floating above it. */
 stats?: StatStripItem[]
 /** When the dashboard is scoped to one project, shown as the card subtitle. */
 scopedProjectName?: string | null
}

// The time-period selector lives in the card header (a dropdown) and is the
// single scope control for this view — it replaces the old floating "All time"
// subtitle and the redundant 30d/90d/All button row.
type Window = '30' | '90' | 'all'

const WINDOW_OPTIONS: { value: Window; label: string }[] = [
 { value: '30', label: 'Last 30 days' },
 { value: '90', label: 'Last 90 days' },
 { value: 'all', label: 'All time' },
]

export function CostOverTimeChart({ daily, stats, scopedProjectName }: Props) {
 const [window, setWindow] = useState<Window>('all')

 const { data, models, dateFormatter } = useMemo(() => {
  const sorted = [...daily].sort((a, b) => a.date.localeCompare(b.date))
  const sliced = window === 'all' ? sorted : sorted.slice(-Number(window))
  const modelSet = new Set<string>()
  for (const d of sliced) Object.keys(d.costs ?? {}).forEach((m) => modelSet.add(m))
  const models = [...modelSet]
  const dates = sliced.map((d) => d.date)
  return {
   data: sliced.map((d) => ({
    date: d.date,
    ...Object.fromEntries(models.map((m) => [m, d.costs[m] ?? 0])),
    total: d.total,
   })),
   models,
   dateFormatter: makeDateTickFormatter(dates),
  }
 }, [daily, window])

 return (
  <Card className="min-w-0 gap-0 overflow-hidden py-0">
   <SectionTitle
    variant="card"
    title="Cost Over Time"
    subtitle={scopedProjectName ? `Scoped to ${scopedProjectName}` : undefined}
    action={
     <Select value={window} onValueChange={(v) => setWindow(v as Window)}>
      <SelectTrigger size="sm" className="w-[132px]">
       <SelectValue />
      </SelectTrigger>
      <SelectContent>
       {WINDOW_OPTIONS.map((o) => (
        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
       ))}
      </SelectContent>
     </Select>
    }
   />
   {stats && stats.length > 0 ? <StatStrip items={stats} variant="table" /> : null}
   <CardContent className="py-4">
    <ResponsiveContainer width="100%" height={200}>
    <AreaChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
     <ChartGrid />
     <ChartXAxis
      dataKey="date"
      tickFormatter={dateFormatter}
      interval="preserveStartEnd"
      minTickGap={50}
     />
     <ChartYAxis tickFormatter={(v: number) => formatCost(v)} width={64} />
     <Tooltip
      contentStyle={CHART_TOOLTIP_STYLE}
      labelFormatter={(label) => formatDateFull(String(label))}
      formatter={(val, name) => [
       formatCost(typeof val === 'number' ? val : 0),
       shortModelName(typeof name === 'string' ? name : ''),
      ]}
     />
     {models.map((m) => (
      <Area
       key={m}
       type="monotone"
       dataKey={m}
       stackId="1"
       stroke={colorForModel(m)}
       fill={colorForModel(m)}
       fillOpacity={0.19}
       strokeWidth={1.5}
       isAnimationActive={false}
      />
     ))}
    </AreaChart>
   </ResponsiveContainer>
   </CardContent>
  </Card>
 )
}
